/**
 * JWT token storage.
 *
 * Access token: kept in memory only (never persisted) so an XSS payload can't
 * read it from storage and it dies with the tab. Short-lived (15 min, per the
 * backend SIMPLE_JWT config).
 *
 * Refresh token: persisted to localStorage so a page reload can silently
 * re-mint an access token (otherwise every refresh would force a re-login).
 * This is the pragmatic default given the backend issues both tokens in the
 * JSON body (rest_framework_simplejwt.TokenObtainPairView). The hardened
 * variant the plan flags as recommended — refresh in an httpOnly,
 * SameSite=Strict cookie — needs a backend endpoint that sets the cookie; it
 * is a documented follow-up. All refresh access goes through this module so
 * swapping the backing store later touches one file.
 */

const REFRESH_KEY = "activateur.refresh";

let accessToken: string | null = null;

type Listener = () => void;
const listeners = new Set<Listener>();

function notify(): void {
  for (const l of listeners) l();
}

export function onTokensChanged(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function getRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

export function setTokens(tokens: { access?: string | null; refresh?: string | null }): void {
  if (tokens.access !== undefined) {
    accessToken = tokens.access;
  }
  if (tokens.refresh !== undefined) {
    try {
      if (tokens.refresh) localStorage.setItem(REFRESH_KEY, tokens.refresh);
      else localStorage.removeItem(REFRESH_KEY);
    } catch {
      /* storage unavailable (private mode / disabled) — refresh won't survive reload */
    }
  }
  notify();
}

export function clearTokens(): void {
  accessToken = null;
  try {
    localStorage.removeItem(REFRESH_KEY);
  } catch {
    /* ignore */
  }
  notify();
}

export function hasSession(): boolean {
  return accessToken !== null || getRefreshToken() !== null;
}
